import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { StockAccuracyResponse, useAsyncAction, useRfid } from '../../../../../hooks';
import { Container, Icon, Icons, Typography } from '../../../../../stories/atoms';
import { DropdownMenu } from '../../../../../stories/molecules';
import { muiTheme } from '../../../../../theme';
import { homeTranslations } from '../../../../../translations';
import { isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { isMonthlyView, isWeeklyView } from '../../../util';
import { HeaderContainer, Section, Wrapper } from './StockAccuracyWidget.styles';
import { StockAccuracyCard } from './stockAccuracyCard/StockAccuracyCard';
import { StockAccuracyModal } from './stockAccuracyModal/StockAccuracyModal';
import { getAggregateStockAccuracyData } from './util';

type StockAccuracyWidgetProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
};

export const StockAccuracyWidget: FC<StockAccuracyWidgetProps> = observer(({ currentDate, interval }) => {
  const { t } = useTranslation();

  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));

  const { stockAccuracy: stockAccuracyTranslations, rfidApprovals: rfidApprovalsTranslations } = homeTranslations;
  const { fetchStockAccuracyData } = useRfid();

  const isMonthly = isMonthlyView(interval);
  const [isStockAccuracyModalOpen, setIsStockAccuracyModalOpen] = useState(false);

  const {
    result: stockAccuracyDetails,
    isLoading: stockAccuracyLoading,
    isError: stockAccuracyError,
    triggerAction: triggerFetchStockAccuracy,
  } = useAsyncAction<StockAccuracyResponse>();

  const fetchData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchStockAccuracyData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [interval, currentDate]);

  const fetchWidgetData = () => {
    triggerFetchStockAccuracy(fetchData);
  };

  useEffect(() => {
    if (isMonthlyView(interval)) return;
    fetchWidgetData();
  }, [currentDate]);

  const stockAggregateData = useMemo(() => {
    return getAggregateStockAccuracyData(stockAccuracyDetails?.stockAccuracy ?? []);
  }, [stockAccuracyDetails?.stockAccuracy]);
  const noData = !stockAccuracyDetails?.stockAccuracy?.length;

  const isShowDetailsDisabled = interval === StorePerformanceInterval.DAILY || isWeeklyView(interval) || noData;

  const menuItems = [
    {
      key: 'showDetails',
      disabled: isShowDetailsDisabled,
      hasTooltip: isShowDetailsDisabled,
      tooltipText: (
        <Container padding={[1]}>
          <Typography color="white">
            {noData
              ? t(stockAccuracyTranslations.noData.key, stockAccuracyTranslations.noData.defaultValue)
              : t(
                  stockAccuracyTranslations.dropdownTooltip.key,
                  stockAccuracyTranslations.dropdownTooltip.defaultValue,
                )}
          </Typography>
        </Container>
      ),
      item: (
        <Typography margin={[0, 2]}>
          {t(stockAccuracyTranslations.showDetails.key, stockAccuracyTranslations.showDetails.defaultValue)}
        </Typography>
      ),
    },
  ];

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(stockAccuracyTranslations.title.key, stockAccuracyTranslations.title.defaultValue)}
      subHeaderText={t(stockAccuracyTranslations.subTitle.key, stockAccuracyTranslations.subTitle.defaultValue)}
      toolTipText={t(stockAccuracyTranslations.toolTip.key, stockAccuracyTranslations.toolTip.defaultValue)}
      isLoading={stockAccuracyLoading}
      isError={stockAccuracyError}
      errorText={t(stockAccuracyTranslations.error.key, stockAccuracyTranslations.error.defaultValue)}
      errorRetryHandler={fetchWidgetData}
      lastUpdatedDate={stockAccuracyDetails?.lastUpdated}
      noDataText={t(stockAccuracyTranslations.noData.key, stockAccuracyTranslations.noData.defaultValue)}
      isNoData={noData}
      isMonthlyViewDisabled={isMonthly}
      headerSideContent={
        <HeaderContainer>
          <DropdownMenu
            data-testid="dropdown"
            dropdownLabel={''}
            buttonContentProps={{
              iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
            }}
            menuItems={menuItems}
            minWidth={'0px'}
            isCompact
            initialSelected={[]}
            onSelect={(selectedItems) => {
              if (selectedItems[0] === 'showDetails') {
                setIsStockAccuracyModalOpen(true);
              }
            }}
            isOnlyIcon
            allowTooltip
          />
        </HeaderContainer>
      }
      content={
        <>
          {isMonthlyView(interval) && (
            <StockAccuracyModal
              stockAccuracyData={stockAccuracyDetails?.stockAccuracy ?? []}
              isOpen={isStockAccuracyModalOpen}
              onClose={() => {
                setIsStockAccuracyModalOpen(false);
              }}
            />
          )}
          <Wrapper isMobile={isMobileView} data-testid="stock-accuracy-widget-wrapper">
            <Section isMobile={isMobileView}>
              <StockAccuracyCard
                key="stock-accuracy"
                accuracyIcon={Icons.DELIVERY}
                accuracyTitle={t(
                  stockAccuracyTranslations.title.key,
                  stockAccuracyTranslations.title.defaultValue,
                )}
                accuracyText={t(
                  stockAccuracyTranslations.accuracyText.key,
                  stockAccuracyTranslations.accuracyText.defaultValue,
                )}
                accuracyValue={stockAggregateData.stockAccuracyPercentage}
                showColor
              />
              <StockAccuracyCard
                key="positive-difference"
                accuracyIcon={Icons.ARROW}
                rotate="right"
                accuracyTitle={t(
                  rfidApprovalsTranslations.positiveTitle.key,
                  rfidApprovalsTranslations.positiveTitle.defaultValue,
                )}
                accuracyText={t(
                  stockAccuracyTranslations.positiveDifferenceText.key,
                  stockAccuracyTranslations.positiveDifferenceText.defaultValue,
                )}
                accuracyValue={stockAggregateData.positiveDifference}
              />
            </Section>
            <Section isMobile={isMobileView}>
              <StockAccuracyCard
                key="negative-difference"
                accuracyIcon={Icons.ARROW}
                rotate="left"
                accuracyTitle={t(
                  rfidApprovalsTranslations.negativeTitle.key,
                  rfidApprovalsTranslations.negativeTitle.defaultValue,
                )}
                accuracyText={t(
                  stockAccuracyTranslations.negativeDifferenceText.key,
                  stockAccuracyTranslations.negativeDifferenceText.defaultValue,
                )}
                accuracyValue={stockAggregateData.negativeDifference}
              />

              <StockAccuracyCard
                key="open-orders"
                accuracyIcon={Icons.PLUS}
                accuracyTitle={t(
                  stockAccuracyTranslations.openOrders.key,
                  stockAccuracyTranslations.openOrders.defaultValue,
                )}
                accuracyText={t(
                  stockAccuracyTranslations.openOrderText.key,
                  stockAccuracyTranslations.openOrderText.defaultValue,
                )}
                accuracyValue={stockAggregateData.openOrderQuantity}
              />
            </Section>
          </Wrapper>
        </>
      }
    />
  );
});
